import React from 'react';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import Divider from '@/components/atom/Divider';
import QnaStickyWriteButton from '@/components/common/qna-sticky-write-button';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyMissionHeader from '@/components/molecules/StickyMissionHeader';
import BottomActionSheet from '@/components/molecules/bottom-action-sheet';
import ReportSelectModal from '@/components/molecules/modal/select-modal/report-select-modal';
import StickyAppHeader from '@/components/molecules/sticky-app-header';
import AppInstallTopBanner from '@/components/templates/mobile/communication/common/AppInstallTopBanner';

import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import useQuestionDetailViewModel from '@/hooks/qna/useQuestionDetailViewModel';
import { AdPageType } from '@/models/ad/list';
import { isQnaMyQuestionPaid, isQnaMyQuestionPrivate } from '@/services/qna/mobile';
import { AdBandCta, GoogleAdSense, NaverAdComponent, UnifiedAdBanner } from '@/shared/ui';
import { processContentForNaverAd, processTitleForNaverAd } from '@/shared/utils/adContent';
import theme from '@/styles/theme';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { QnaDetailVo } from '@/types/qna';
import { historyBack } from '@/utils/mobile/RNPlugins';
import { cn } from '@/utils/style';
import styled from 'styled-components';
import NearQuestions from './near-questions';
import QnaAnswers from './qna-answers';
import QnaRelatedQuestion from './qna-related-question';
import QuestionDetail from './question-detail';
import QuestionPaymentInfo from './question-payment-info/QuestionPaymentInfo';

const NAVER_AD_CHANNEL = 'm_doctornow.ch1';
const NAVER_AD_KEYWORD_GROUP = '모바일닥터나우_공통';

interface Props extends CustomPageProps {
  viewModel: Omit<ReturnType<typeof useQuestionDetailViewModel>, 'qnaDetail'> & {
    qnaDetail: QnaDetailVo;
  };
  topBannerViewModel: ReturnType<typeof useAppInstallTopBanner>;
}

const QnaDetailMobileTemplate = ({ viewModel, client, topBannerViewModel }: Props) => {
  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title={viewModel.qnaDetail.category.name} onClickBackButton={viewModel.goBack} />
        </TemplateLayout.Header>
      )}
      {!topBannerViewModel.isNewAdVisible &&
        client.clientType === ClientType.MOBILE_WEB &&
        topBannerViewModel.isVisible && (
          <TemplateLayout.StickyHeader
            style={{
              top: 60,
            }}
          >
            <AdBandCta
              client={client}
              onClose={topBannerViewModel.hideBanner}
              enableAnimation={true}
              fallbackContent={
                <AppInstallTopBanner
                  onLogEvent={viewModel.onClickTopCta}
                  onClickClose={topBannerViewModel.hideBanner}
                  content='web_content_bnr_service'
                  trackingInfo={{
                    campaign: 'web_content_qna',
                    contentId: viewModel.qnaDetail.questionPid,
                    contentTitle: viewModel.qnaDetail.title,
                    contentCategory: viewModel.qnaDetail.category.name,
                    contentSubCategory: `QNA@${viewModel.qnaDetail.category.type}@${viewModel.qnaDetail.category.questionCategoryId}`,
                  }}
                />
              }
            />
          </TemplateLayout.StickyHeader>
        )}
      {topBannerViewModel.isNewAdVisible && topBannerViewModel.isNewAdVisibleClient(client) && (
        <TemplateLayout.StickyHeader
          style={{
            top: 60,
          }}
          isVisible={topBannerViewModel.isScrollVisible}
        >
          <UnifiedAdBanner
            adPageType={AdPageType.TopCommonBanner}
            client={client}
            fallbackContent={
              <GoogleAdSense
                client={client}
                adPageType={AdPageType.TopCommonBanner}
                wrapperClassName={cn('h-[72px] flex w-full items-center justify-center')}
                style={{
                  width: 320,
                  height: 50,
                }}
              />
            }
          />
        </TemplateLayout.StickyHeader>
      )}
      {client.isWebView && viewModel.timerVisible && (
        <StickyMissionHeader
          displayTime={viewModel.displayTime}
          targetTime={viewModel.missionTargetTime}
          missionCompleted={viewModel.missionCompleted}
          clickable={viewModel.missionCompleted}
          onClick={historyBack}
        />
      )}
      <TemplateLayout.Content>
        {client.clientType === ClientType.WEB && (
          <BreadCrumbsWrapper>
            <BreadCrumbs
              visible
              name={viewModel.qnaDetail.category.name}
              path={`${ROUTES.QNA.DETAIL.path}/realtime`}
              type='qna'
              urlParameter={`?categoryId=${viewModel.qnaDetail.category.questionCategoryId}`}
            />
          </BreadCrumbsWrapper>
        )}
        <Container isWebview={client.isWebView}>
          {/* 질문 */}
          <QuestionDetail
            questionDetail={viewModel.qnaDetail}
            onShowModal={viewModel.onShowModal}
            qnaAttachments={viewModel.qnaAttachments}
            onClickImage={viewModel.onClickImage}
            imageZoomIndex={viewModel.imageZoomIndex}
            onCloseImageZoom={viewModel.onCloseImageZoom}
            onClickLikeQuestion={viewModel.onClickLikeQuestion}
            pageName={viewModel.pageName}
            onShareModal={viewModel.onShareModal}
            onChangeShowShareModal={viewModel.onChangeShowShareModal}
            user={viewModel.user}
            imageSlideIdx={viewModel.imageSlideIdx}
            onChangeImageBlindType={viewModel.onChangeImageBlindType}
            onChangeSlideImage={viewModel.onChangeSlideImage}
            isMyQuestion={viewModel.isMyQuestion}
          />

          {!client.isWebView && (
            <UnifiedAdBanner
              client={client}
              adPageType={AdPageType.QnaDetailQuestionBottomBanner}
              borderRadius={8}
              className='px-[24px]'
              fallbackContent={
                <GoogleAdSense
                  client={client}
                  adPageType={AdPageType.QnaDetailQuestionBottomBanner}
                  wrapperClassName={cn('my-[16px] flex aspect-[360/160] w-full items-center justify-center')}
                  className='w-full'
                  responsive
                />
              }
            />
          )}

          <Divider height={'15px'} color={theme.color.gray30} />
          {/* 답변 */}
          {!!viewModel.qnaDetail.answers.length && (
            <>
              <QnaAnswers
                qnaDetail={viewModel.qnaDetail}
                categoryName={viewModel.qnaDetail.category.name}
                onShowModal={viewModel.onShowModal}
                onClickLikeAnswer={viewModel.onClickLikeAnswer}
                onClickMagazine={viewModel.onClickMagazine}
                onClickWriteReview={viewModel.onClickWriteReview}
                moveToHospitalDetail={viewModel.moveToHospitalDetail}
                moveToDoctorDetailPage={viewModel.moveToDoctorDetailPage}
                onClickOuterLink={viewModel.onClickOuterLink}
                questionPid={viewModel.qnaDetail.questionPid}
                extractTextFromChildren={viewModel.extractTextFromChildren}
                client={client}
              />
              <UnifiedAdBanner
                className={'px-[24px] py-[12px]'}
                adPageType={AdPageType.QnaDetailAnswerBottomBanner}
                contextId={viewModel.qnaDetail.category.name}
                client={client}
                borderRadius={8}
                fallbackContent={
                  <GoogleAdSense
                    client={client}
                    adPageType={AdPageType.QnaDetailAnswerBottomBanner}
                    wrapperClassName={cn('my-[12px] flex w-full items-center justify-center', {
                      'h-[250px]': client.isMobile,
                      'h-[90px]': !client.isMobile,
                    })}
                    style={{
                      width: client.isMobile ? 300 : 728,
                      height: client.isMobile ? 250 : 90,
                    }}
                  />
                }
              />
            </>
          )}

          {viewModel.isMyQuestion &&
            viewModel.qnaDetail.paymentInfo &&
            isQnaMyQuestionPaid(viewModel.qnaDetail.status) && (
              <QuestionPaymentInfo
                paidAt={viewModel.qnaDetail.paymentInfo.paidAt}
                price={viewModel.qnaDetail.paymentInfo.price}
                discountPrice={viewModel.qnaDetail.paymentInfo.discountAmount}
                cardName={viewModel.qnaDetail.paymentTotal?.userPayment?.cardName || ''}
                cardNumber={viewModel.qnaDetail.paymentTotal?.userPayment?.cardNumber || ''}
                pointUsed={viewModel.qnaDetail.paymentInfo.usedPoint}
                paidAmount={viewModel.qnaDetail.paymentInfo.paidAmount}
              />
            )}

          {/* 관련 질문 */}
          <QnaRelatedQuestion
            categoryName={viewModel.qnaDetail.category.name}
            relatedQna={viewModel.relatedQna}
            onClickRelatedQna={viewModel.onClickRelatedQna}
          />

          {/* 이전/다음 글 */}
          <NearQuestions
            nearQuestions={viewModel.qnaDetail.nearSimpleQuestions}
            onClickNearQna={viewModel.onClickNearQna}
          />

          {/* 네이버 광고 */}
          <NaverAdComponent
            channel={NAVER_AD_CHANNEL}
            title={processTitleForNaverAd(viewModel.qnaDetail.title)}
            content={processContentForNaverAd(viewModel.qnaDetail.content)}
            keywordGroup={NAVER_AD_KEYWORD_GROUP}
          />

          {!client.isWebView && (
            <UnifiedAdBanner
              adPageType={AdPageType.QnaDetailBottomBanner}
              borderRadius={client.isMobile ? 8 : 0}
              className={cn({
                'px-[24px]': client.isMobile,
              })}
              client={client}
              fallbackContent={
                <GoogleAdSense
                  client={client}
                  wrapperClassName={cn('my-[16px] flex h-[280px] w-full items-center justify-center')}
                  style={{
                    width: 336,
                    height: 280,
                  }}
                  adPageType={AdPageType.QnaDetailBottomBanner}
                />
              }
            />
          )}

          {/* 질문 신고하기 */}
          {viewModel.reportItems && (
            <ReportSelectModal
              isVisible={viewModel.showReportModal}
              onClose={viewModel.onCloseReportModal}
              selectList={viewModel.reportItems}
            />
          )}

          {/* 질문 비공개하기 */}
          <BottomActionSheet
            isOpened={viewModel.isShowPrivateModal}
            onClickOverlay={viewModel.toggleIsShowPrivateModal}
          >
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col gap-2 px-3'>
                <div className='typography-ui-20sb text-gray-800'>내 질문 관리</div>
                <div className='typography-ui-15r text-gray-600'>비공개로 전환 후 다시 공개로 되돌릴 수 없어요.</div>
              </div>
              <button
                className={cn([
                  'typography-ui-17m flex w-full justify-start px-3 py-4 text-gray-600',
                  'disabled:text-gray-100',
                ])}
                disabled={isQnaMyQuestionPrivate(viewModel.qnaDetail.accessibleType)}
                onClick={viewModel.onClickQuestionPrivate}
              >
                비공개로 전환하기
              </button>
            </div>
          </BottomActionSheet>
        </Container>
        <QnaStickyWriteButton
          buttonText={viewModel.getStickyButtonText()}
          pageName={viewModel.pageName}
          sectionName={viewModel.pageName}
          shouldShowEventText={viewModel.shouldShowEventText()}
          onClickButton={viewModel.onClickStickButton}
          questionCategory={viewModel.targetQuestionCategory}
        />
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default QnaDetailMobileTemplate;

const Container = styled.div<{ isWebview: boolean }>`
  ${({ theme, isWebview }) => `
    width: 100%;
    height: auto;
    min-height: ${isWebview ? '100vh' : '0'};
    background: ${theme.color.white};
  `};

  position: relative;

  /* CLS 개선을 위한 레이아웃 안정화 */
  contain: layout style;
  will-change: auto;

  /* 하위 요소들의 레이아웃 시프트 최소화 */
  & > * {
    contain: layout;
  }
`;

const BreadCrumbsWrapper = styled.div`
  padding: 26px 24px 0 24px;
`;
